import React from 'react';
import { jsPDF } from 'jspdf';
import '../../assets/fonts/arial-normal';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  Footer,
} from 'docx';
import saveAs from 'file-saver';

export interface RelievingLetterProps {
  details: {
    employeeName: string;
    email: string;
    designation: string;
    department: string;
    role: string;
    employmentType: string;
    employmentStatus: string;
    dateOfJoining: string;
    relievingDate: string;
    companyName: string;
    companyAddress: string;
    hrName: string;
    hrDesignation: string;
  };
  isDoc?: boolean;
}

function AiRelievingLetter({ details, isDoc }: RelievingLetterProps) {
  const {
    employeeName,
    email,
    designation,
    department,
    role,
    employmentType,
    employmentStatus,
    dateOfJoining,
    relievingDate,
    companyName,
    companyAddress,
    hrName,
    hrDesignation,
  } = details;

  const formattedJoiningDate = new Date(dateOfJoining).toLocaleDateString(
    'en-IN',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }
  );
  const formattedRelievingDate = new Date(relievingDate).toLocaleDateString(
    'en-IN',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }
  );

  const generatePDF = () => {
    const doc = new jsPDF();
    const marginLeft = 20;
    const lineSpacing = 6;
    let cursorY = 30;

    doc.setFont('arial', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(119, 104, 187);
    doc.text('RELIEVING LETTER', marginLeft, cursorY);

    doc.setFont('arial', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    cursorY += lineSpacing * 2;

    doc.text(`Date: ${formattedRelievingDate}`, marginLeft, cursorY);
    cursorY += lineSpacing;

    doc.text(`To,`, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.text(`${employeeName}`, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.text(`Email: ${email}`, marginLeft, cursorY);
    cursorY += lineSpacing;

    doc.text(
      `Subject: Confirmation of Relieving from Duties`,
      marginLeft,
      cursorY
    );
    cursorY += lineSpacing * 2;

    doc.text(`Dear ${employeeName.split(' ')[0]},`, marginLeft, cursorY);
    cursorY += lineSpacing * 1.5;

    const body = `This letter serves as a formal confirmation that your resignation from the position of ${designation} at ${companyName} has been accepted. Your last working day with the organization was on ${formattedRelievingDate}. We appreciate the notice provided and acknowledge the completion of your duties with professionalism and respect.

You were employed at ${companyName} from ${formattedJoiningDate} to ${formattedRelievingDate}, and during this time, we found your performance and conduct to be consistently professional, reliable, and aligned with the values and mission of the organization. Your work ethic, commitment, and contribution to the team's goals were valued and acknowledged by your peers and supervisors alike.

We are pleased to inform you that all required clearance procedures, including the handover of responsibilities, return of company property, and completion of final formalities, have been duly fulfilled. As such, there are no outstanding obligations from your end as of your last working date.

Please consider this letter as an official confirmation that you have been relieved from all duties and responsibilities associated with your role. This also certifies that your exit from the organization has been completed in good standing.

We would like to take this opportunity to thank you for your services and dedication. Your presence and positive contributions have been appreciated, and you will always remain a part of our extended family. We hope you will carry forward the same spirit of integrity and excellence into your future endeavors.

Should you require any further clarification or assistance regarding your tenure or documentation for future employment purposes, please do not hesitate to get in touch with the HR department.

We wish you all the best in your career and personal life. May you achieve continued success and fulfillment in all your future pursuits.`;

    const bodyLines = doc.splitTextToSize(body, 170);
    doc.text(bodyLines, marginLeft, cursorY, {
      align: 'left',
      maxWidth: 170,
    });
    cursorY += bodyLines.length * 4.5 + lineSpacing;

    doc.setFont('arial', 'bold');
    doc.text(`Sincerely,`, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.setFont('arial', 'normal');
    doc.text(`${hrName}`, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.text(`${hrDesignation}`, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.text(`${companyName}`, marginLeft, cursorY);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const baseHeight = 12;
    const topHeight = 8;

    doc.setFillColor('#8073C0');
    doc.rect(0, pageHeight - baseHeight, pageWidth, baseHeight, 'F');
    doc.setFillColor('#323595');
    doc.rect(
      0,
      pageHeight - baseHeight - topHeight + 4,
      pageWidth,
      topHeight,
      'F'
    );

    const filename = `${employeeName.replace(/\s+/g, '_')}_relieving_letter.pdf`;
    doc.save(filename);
  };

  const bodyParagraphs = [
    `This letter serves as a formal confirmation that your resignation from the position of ${designation} at ${companyName} has been accepted. Your last working day with the organization was on ${formattedRelievingDate}. We appreciate the notice provided and acknowledge the completion of your duties with professionalism and respect.`,
    `You were employed at ${companyName} from ${formattedJoiningDate} to ${formattedRelievingDate}, and during this time, we found your performance and conduct to be consistently professional, reliable, and aligned with the values and mission of the organization.`,
    `Your work ethic, commitment, and contribution to the team's goals were valued and acknowledged by your peers and supervisors alike.`,
    `We are pleased to inform you that all required clearance procedures, including the handover of responsibilities, return of company property, and completion of final formalities, have been duly fulfilled. As such, there are no outstanding obligations from your end as of your last working date.`,
    `Please consider this letter as an official confirmation that you have been relieved from all duties and responsibilities associated with your role. This also certifies that your exit from the organization has been completed in good standing.`,
    `We would like to take this opportunity to thank you for your services and dedication. Your presence and positive contributions have been appreciated, and you will always remain a part of our extended family. Should you require any further clarification or assistance regarding your tenure or documentation for future employment purposes, please get in touch with the HR department. We wish you all the best in your career and personal life. May you achieve continued success and fulfillment in all your future pursuits.`,
  ];

  const downloadDocx = async () => {
    const docxDocument = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: { top: 1940, bottom: 720, left: 0, right: 0, footer: 0 },
            },
          },
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  children: [
                    new Table({
                      width: { size: 100, type: WidthType.PERCENTAGE },
                      rows: [
                        new TableRow({
                          children: [
                            new TableCell({
                              margins: { left: 0, right: 0, top: 0, bottom: 0 },
                              shading: { fill: '323595' },
                              borders: {
                                top: { style: BorderStyle.NONE },
                                bottom: { style: BorderStyle.NONE },
                                left: { style: BorderStyle.NONE },
                                right: { style: BorderStyle.NONE },
                              },
                              children: [],
                            }),
                          ],
                        }),
                        new TableRow({
                          children: [
                            new TableCell({
                              margins: { left: 0, right: 0, top: 0, bottom: 0 },
                              shading: { fill: '8073C0' },
                              borders: {
                                top: { style: BorderStyle.NONE },
                                bottom: { style: BorderStyle.NONE },
                                left: { style: BorderStyle.NONE },
                                right: { style: BorderStyle.NONE },
                              },
                              children: [],
                            }),
                          ],
                        }),
                      ],
                    }),
                  ],
                  alignment: AlignmentType.LEFT,
                  spacing: { before: 0, after: 0 },
                }),
              ],
            }),
          },
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: 'RELIEVING LETTER',
                  bold: true,
                  font: 'Arial',
                  size: 32,
                  color: '775CBB',
                }),
              ],
              spacing: { after: 120 },
              alignment: AlignmentType.LEFT,
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `Date: ${formattedRelievingDate}`,
                  font: 'Arial',
                  size: 26,
                }),
              ],
              indent: { left: 720, right: 720 },
              spacing: { after: 120 },
            }),
            new Paragraph({
              children: [new TextRun({ text: 'To:', font: 'Arial', size: 26 })],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: employeeName, font: 'Arial', size: 26 }),
              ],
              indent: { left: 720, right: 720 },
              spacing: { after: 120 },
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `Email: ${email}`,
                  font: 'Arial',
                  size: 26,
                }),
              ],
              indent: { left: 720, right: 720 },
              spacing: { after: 120 },
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Subject: Confirmation of Relieving from Duties',
                  bold: true,
                  font: 'Arial',
                  size: 26,
                }),
              ],
              indent: { left: 720, right: 720 },
              spacing: { after: 240 },
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `Dear ${employeeName.split(' ')[0]},`,
                  bold: true,
                  font: 'Arial',
                  size: 26,
                }),
              ],
              indent: { left: 720, right: 720 },
              spacing: { after: 120 },
            }),
            ...bodyParagraphs.map(
              (para) =>
                new Paragraph({
                  children: [
                    new TextRun({ text: para, font: 'Arial', size: 24 }),
                  ],
                  indent: { left: 720, right: 720 },
                  alignment: AlignmentType.JUSTIFIED,
                  spacing: { after: 200, line: 276 },
                })
            ),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Sincerely,',
                  bold: true,
                  font: 'Arial',
                  size: 24,
                }),
              ],
              indent: { left: 720, right: 720 },
              spacing: { after: 120 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: hrName, font: 'Arial', size: 24 }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: hrDesignation, font: 'Arial', size: 24 }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: companyName, font: 'Arial', size: 24 }),
              ],
              indent: { left: 720, right: 720 },
            }),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(docxDocument);
    const filename = `${employeeName.replace(
      /\s+/g,
      '_'
    )}_relieving_letter.docx`;
    saveAs(blob, filename);
  };

  return (
    <div>
      <button
        onClick={isDoc ? downloadDocx : generatePDF}
        style={{
          backgroundColor: '#8254ff',
          color: 'white',
          padding: '10px 16px',
          fontSize: '14px',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
        }}
      >
        Download Relieving Letter
      </button>
    </div>
  );
}

export default AiRelievingLetter;
